import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  addressBookService,
  AddressBookData,
  orderService,
} from "../services";
import OrderSuccess from "../modal/OrderSuccess";
import arrow from "../assets/images/order/orange-arrow.png";

// Aligned with mobile App/Screens/Resturant/GroupOrderCheckout.tsx
const GroupOrderCheckout = () => {
  const navigate = useNavigate();
  const { sessionId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const restaurantId =
    searchParams.get("restaurantId") || "";

  const [loading, setLoading] = useState(true);
  const [orderLoading, setOrderLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<any>(null);
  const [selectedAddress, setSelectedAddress] =
    useState<AddressBookData | null>(null);
  const [addresses, setAddresses] = useState<AddressBookData[]>([]);
  const [pricing, setPricing] = useState<{
    deliveryFee: number;
    taxAmount: number;
    totalAmount: number;
    subtotal?: number;
  } | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!sessionId) {
        toast.error("Invalid session");
        navigate(-1);
        return;
      }
      try {
        setLoading(true);
        const [sessionRes, addressRes]: any[] = await Promise.all([
          orderService.getGroupOrderSession(sessionId),
          addressBookService.getAllAddresses(),
        ]);

        if (sessionRes.status && sessionRes.data) {
          setSessionData(sessionRes.data);
          if (!restaurantId && sessionRes.data.session?.restaurantId) {
            // restaurantId from session if missing in query
          }
        }

        if (addressRes.status && addressRes.data) {
          setAddresses(addressRes.data);
          const defaultAddr =
            addressRes.data.find((a: AddressBookData) => a.isDefault) ||
            addressRes.data[0] ||
            null;
          // Prefer session delivery address match
          const sessionAddr = sessionRes?.data?.session?.deliveryAddress;
          if (sessionAddr) {
            const match = addressRes.data.find(
              (a: AddressBookData) =>
                a.addressLine1 === sessionAddr.addressLine1 &&
                a.phone === sessionAddr.phone
            );
            setSelectedAddress(match || defaultAddr);
          } else {
            setSelectedAddress(defaultAddr);
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load checkout");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionId, navigate, restaurantId]);

  const resolvedRestaurantId =
    restaurantId || sessionData?.session?.restaurantId || "";

  useEffect(() => {
    if (!sessionData || !resolvedRestaurantId) {
      setPricing(null);
      return;
    }

    const fetchPricing = async () => {
      try {
        const payload = {
          restaurantId: resolvedRestaurantId,
          items: sessionData.items.map((item: any) => ({
            dishId: item.dish?._id ?? item.dishId,
            variantId: null,
            addons: [],
            quantity: item.quantity,
          })),
          paymentMethod: "cod",
        };
        const res: any = await orderService.getPricing(payload);
        if (res.status && res.data) {
          setPricing({
            deliveryFee: res.data.deliveryFee,
            taxAmount: res.data.taxAmount,
            totalAmount: res.data.totalAmount,
            subtotal: res.data.subtotal,
          });
        }
      } catch (err) {
        console.error("Error fetching pricing:", err);
      }
    };
    fetchPricing();
  }, [sessionData, resolvedRestaurantId]);

  const subtotal =
    pricing?.subtotal ??
    (sessionData?.items || []).reduce(
      (sum: number, item: any) =>
        sum + (item.dish?.basePrice ?? 0) * item.quantity,
      0
    );
  const deliveryCharges = pricing?.deliveryFee ?? 0;
  const taxAmount = pricing?.taxAmount ?? 0;
  const total =
    pricing?.totalAmount ?? subtotal + deliveryCharges + taxAmount;

  const handlePlaceOrder = async () => {
    if (!selectedAddress || !sessionData) {
      toast.error("Please select a delivery address");
      return;
    }

    setOrderLoading(true);
    try {
      const data = {
        sessionId,
        restaurantId: resolvedRestaurantId,
        paymentMethod: "cod",
        items: sessionData.items.map((item: any) => ({
          dishId: item.dish?._id ?? item.dishId,
          variantId: item.variantId ?? null,
          addons: item.addons ?? [],
          quantity: item.quantity,
        })),
        deliveryAddress: {
          fullName: selectedAddress.fullName,
          phone: selectedAddress.phone,
          addressLine1: selectedAddress.addressLine1,
          addressLine2: selectedAddress.addressLine2,
          city: selectedAddress.city,
          pincode: selectedAddress.pincode,
          geoLocation: selectedAddress.geoLocation,
        },
      };

      // Mobile placeGroupOrder posts to /order with sessionId
      const res: any = await orderService.createOrder(data);
      if (res.status && res.data) {
        setCreatedOrderId(res.data.orderId);
        setShowSuccess(true);
      } else {
        toast.error(res.message || "Failed to place order");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to place order");
    } finally {
      setOrderLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <OrderSuccess
        onClose={() => {
          setShowSuccess(false);
          navigate("/orders");
        }}
        orderId={createdOrderId || undefined}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 mt-[72px]">
        <p className="dark:text-white">Loading checkout...</p>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 mt-[72px]">
        <p className="text-red-500">Failed to load group order details</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen mt-[7.5rem] md:mt-[72px] p-4 md:p-8 transition-colors">
      <h1 className="text-[#050505] dark:text-white font-urbanist text-[24px] font-semibold mb-6">
        Group Order Summary
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border-2 dark:border-gray-700 rounded-[16px] p-4 bg-white dark:bg-gray-800">
          <h2 className="text-[#050505] dark:text-white font-semibold mb-4 border-b dark:border-gray-700 pb-3">
            Items ({sessionData.items.length})
          </h2>
          <div className="space-y-3">
            {sessionData.items.map((item: any) => (
              <div key={item._id} className="flex justify-between">
                <p className="text-[#050505] dark:text-white text-sm">
                  {item.quantity} x {item.dish?.dishName || "Item"}
                </p>
                <p className="text-[#050505] dark:text-white text-sm">
                  ₹{((item.dish?.basePrice ?? 0) * item.quantity).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="border-2 dark:border-gray-700 rounded-[16px] p-4 bg-white dark:bg-gray-800">
            <h2 className="text-[#050505] dark:text-white font-semibold mb-4 border-b dark:border-gray-700 pb-3">
              Delivery Address
            </h2>
            {addresses.length === 0 ? (
              <button
                onClick={() => navigate("/saved-addresses")}
                className="text-[#7402A7]"
              >
                Add an address
              </button>
            ) : (
              <select
                value={selectedAddress?._id || ""}
                onChange={(e) => {
                  const addr = addresses.find((a) => a._id === e.target.value);
                  setSelectedAddress(addr || null);
                }}
                className="w-full p-3 rounded-[12px] border dark:border-gray-600 bg-[#F5F5F5] dark:bg-gray-700 dark:text-white"
              >
                {addresses.map((addr) => (
                  <option key={addr._id} value={addr._id}>
                    {addr.fullName} — {addr.addressLine1}, {addr.city}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="border-2 dark:border-gray-700 rounded-[16px] p-4 bg-white dark:bg-gray-800">
            <h2 className="text-[#050505] dark:text-white font-semibold mb-4 border-b dark:border-gray-700 pb-3">
              Charges
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between dark:text-white">
                <span>Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between dark:text-white">
                <span>Delivery</span>
                <span>₹{deliveryCharges.toFixed(2)}</span>
              </div>
              <div className="flex justify-between dark:text-white">
                <span>Tax / Platform fee</span>
                <span>₹{taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold text-lg pt-2 border-t dark:border-gray-700 dark:text-white">
                <span>Total</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
            </div>
            <p className="text-[#6A6A6A] dark:text-gray-400 text-xs mt-3">
              Payment: Cash on Delivery (same as mobile group checkout)
            </p>
          </div>

          <button
            onClick={handlePlaceOrder}
            disabled={orderLoading || !selectedAddress}
            className="w-full flex items-center justify-between py-4 px-6 bg-[#7402A7] dark:bg-purple-600 rounded-[150px] text-white font-urbanist text-[18px] font-semibold hover:bg-[#5f0289] transition-colors disabled:opacity-50"
          >
            <span>{orderLoading ? "Placing..." : `Place Order · ₹${total.toFixed(2)}`}</span>
            <div className="h-[42px] w-[42px] flex justify-center items-center bg-white rounded-full">
              <img src={arrow} alt="arrow" className="h-[30px] w-[24px]" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupOrderCheckout;
